import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { orderBy } from 'lodash';
import { useMutation } from '@apollo/client';
import { ACCENT_COLOR_DEFAULT_INDEX_MAP, INSTALLATION_COLOR_LIST } from '../../../const';
import { useBackdropContext } from '../../../context/backdrop';
import { useInstallationContext } from '../../../context/installation';
import {
  ChangeInstallationOrderMutation,
  ChangeInstallationOrderMutationVariables,
  ChangeInstallationsOrderItemInput,
  IntegrationType,
  UserInstallation,
} from '../../../data-access/gql-types/graphql';
import { CHANGE_INSTALLATION_ORDER } from '../../../data-access/mutations/installations';
import { environment } from '../../../environment';
import { useExalusContext } from '../../../integrations/exalus/context';
import { ROUTES } from '../../../routes/routes';
import { useUserProfileStore } from '../../../store/user-profile';
import { InstallationSortable, IntegrationDefaultEnum } from '../../../types';
import { setBodyStyle } from '../../../utils/theme';
import { toastError } from '../../../utils/toast';
import { useCreateInstallation } from '../../installation-create/hooks/use-create-installation';

export const useInstallationList = () => {
  const navigate = useNavigate();
  const { t: tc } = useTranslation('common');
  const { installationList, selectedInstallation, updateInstallationList, setSelectedInstallationId } =
    useInstallationContext();
  const [isValid, setIsValid] = useState<boolean>(false);
  const [sortMode, setSortMode] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [searchVisible, setSearchVisible] = useState<boolean>(false);
  const { resetInstallationCreate } = useCreateInstallation();
  const { setSelectedExalusCredentials } = useExalusContext();
  const [sortedInstallationList, setSortedInstallationList] = useState<InstallationSortable[]>([]);
  const refetchUserMe = useUserProfileStore((s) => s.refetchUserMe);
  const [changeInstallationOrderVariables, setChangeInstallationVariables] = useState<
    ChangeInstallationsOrderItemInput[]
  >([]);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();

  const [changeInstallationOrder, { loading }] = useMutation<
    ChangeInstallationOrderMutation,
    ChangeInstallationOrderMutationVariables
  >(CHANGE_INSTALLATION_ORDER);

  useEffect(() => {
    if (sortedInstallationList.length > 0 && selectedInstallation) {
      setBodyStyle('--color-accent', selectedInstallation?.hexColor);
    } else {
      setBodyStyle(
        '--color-accent',
        INSTALLATION_COLOR_LIST[ACCENT_COLOR_DEFAULT_INDEX_MAP[environment.INTEGRATION_DEFAULT]],
      );
    }
  }, [sortedInstallationList, selectedInstallation, loading]);

  useEffect(() => {
    refetchUserMe();

    setSortedInstallationList(
      orderBy(installationList, 'order').map((x) => ({ id: x.installationId, installation: x })),
    );
  }, [installationList]);

  const onSortSave = () => {
    if (changeInstallationOrderVariables.length) {
      turnOnBackdrop();
      changeInstallationOrder({
        variables: { input: { orders: changeInstallationOrderVariables } },
        onCompleted: (data) => {
          if (data.changeInstallationOrder.ok) {
            const list = installationList.map((installation: UserInstallation) => {
              const order = changeInstallationOrderVariables.find(
                (x) => x.installationId === installation.installationId,
              )?.order;

              return {
                ...installation,
                order: order !== undefined ? order : installation.order,
              };
            });

            updateInstallationList(list);
            setIsValid(false);
            setSortMode(false);

            turnOffBackdrop();
          } else {
            toastError({ content: tc('errors.somethingWentWrong') });
            // TODO errors
          }
        },
        onError: () => turnOffBackdrop(),
      });
    }
  };

  const toggleSort = () => setSortMode(!sortMode);

  const newInstallation = () => {
    resetInstallationCreate();

    if (selectedInstallation?.integrationType === IntegrationType.Exalus) {
      setSelectedInstallationId(undefined);
      setSelectedExalusCredentials(null);

      if (environment.INTEGRATION_DEFAULT === IntegrationDefaultEnum.Exalus) {
        window.location.href = ROUTES.InstallationCreate();
      } else {
        navigate(ROUTES.InstallationCreate());
      }
    } else {
      navigate(ROUTES.InstallationCreate());
    }
  };

  return {
    newInstallation,
    toggleSort,
    onSortSave,
    setChangeInstallationVariables,
    setIsValid,
    setSortedInstallationList,
    setSearchText,
    setSearchVisible,
    sortedInstallationList,
    sortMode,
    isValid,
    loading,
    searchText,
    searchVisible,
  };
};
